import { useLocalStorage } from '@/util/useStorage';
import type {
  ExpandedState,
  Row,
  RowData,
  RowSelectionState,
  TableOptions,
  TableState,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { StateCleanup } from './components/StateCleanup';
import { Context } from './DataProviderContext';
import { DefaultColumnFilter } from './filter/DefaultColumnFilter';
import { FilterProvider } from './filter/FilterContext';
import { useTableState } from './hooks/useTableState';
import type { DataGridAction, ExtendedColumnDef } from './types';
import { useExtendedColumnDefs } from './useExtendedColumnDefs';

type StructuredRowData = RowData & {
  subRows?: StructuredRowData[];
};

export interface BaseDataProviderProps<TData extends RowData> {
  children?: ReactNode;
  columnDefs: ExtendedColumnDef<TData>[];
  initialData: any[];
  initialState?: Partial<TableState>;
  actions?: DataGridAction[];
  enableColumnConfiguration?: boolean;
  isLoading?: boolean;
  isRowHighlighted?: (row: Row<TData>) => boolean;
  isRowClickable?: (row: Row<TData>) => boolean;
  onRowClick?: (row: Row<TData>) => void;
  id?: string;
}

export interface ClientSideDataProviderProps<TData extends RowData>
  extends BaseDataProviderProps<TData> {
  enableFilters?: boolean;
  enableGrouping?: boolean;
  enableSorting?: boolean;
  enablePagination?: boolean;
  enableStructure?: boolean;
  toStructure?: (flatData: TData[]) => StructuredRowData[];
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
}

export const DataProvider = <TData extends RowData>({
  children,
  columnDefs,
  initialData,
  initialState,
  actions = [],
  enableColumnConfiguration = false,
  enableFilters = false,
  enableGrouping = false,
  enableSorting = false,
  enablePagination = false,
  enableStructure = false,
  toStructure,
  isLoading = false,
  isRowClickable,
  isRowHighlighted,
  onRowClick,
  rowSelection,
  onRowSelectionChange,
  // @ts-ignore
  getRowId = (data) => data.id,
  id,
}: ClientSideDataProviderProps<TData>) => {
  const extendedColumnDefs = useExtendedColumnDefs<TData>(columnDefs);
  const { tableState, onStateChange } = useTableState(id, initialState);
  const [expanded, setExpanded] = useLocalStorage<ExpandedState>(
    [id, 'expanded'].join('.'),
    {}
  );
  const data = useMemo(
    () =>
      enableStructure && typeof toStructure === 'function'
        ? toStructure(initialData)
        : initialData,
    [enableStructure, toStructure, initialData]
  );

  const options: TableOptions<TData> = {
    columns: extendedColumnDefs,
    data,
    state: tableState,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    enableFilters: false,
    enableGrouping: false,
    enableRowSelection: false,
    defaultColumn: {
      // @ts-ignore
      Filter: DefaultColumnFilter,
    },
    onStateChange,
  };

  if (enableSorting) {
    options.enableSorting = true;
    options.getSortedRowModel = getSortedRowModel();
  }

  if (enableFilters) {
    options.enableFilters = true;
    options.filterFromLeafRows = false;
    options.getFilteredRowModel = getFilteredRowModel();
    options.getFacetedRowModel = getFacetedRowModel();
    options.getFacetedUniqueValues = getFacetedUniqueValues();
    options.getFacetedMinMaxValues = getFacetedMinMaxValues();
  }

  if (enableGrouping) {
    options.enableGrouping = true;
    options.getGroupedRowModel = getGroupedRowModel();
    options.getExpandedRowModel = getExpandedRowModel();
  }

  if (enablePagination) {
    options.getPaginationRowModel = getPaginationRowModel();
  }

  if (enableStructure) {
    if (options.state === undefined) {
      options.state = {};
    }
    options.state.expanded = expanded;
    // @ts-ignore
    options.onExpandedChange = setExpanded;
    // @ts-ignore
    options.getSubRows = (row) => row.subRows;
    options.getExpandedRowModel = getExpandedRowModel();
    options.filterFromLeafRows = true;
  }

  if (rowSelection && onRowSelectionChange) {
    if (options.state === undefined) {
      options.state = {};
    }
    options.enableRowSelection = true;
    options.state.rowSelection = rowSelection;
    options.onRowSelectionChange = (updater) => {
      const next =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      onRowSelectionChange(next);
    };
    options.getRowId = getRowId;
  }

  const table = useReactTable(options);

  return (
    <Context.Provider
      value={{
        table,
        actions,
        enableColumnConfiguration,
        isLoading,
        isRowClickable,
        isRowHighlighted,
        onRowClick,
        getRowCount: () => table.getExpandedRowModel().rows.length,
        id,
      }}
    >
      <FilterProvider>
        <StateCleanup />
        {children}
      </FilterProvider>
    </Context.Provider>
  );
};
